/**
 * Collect comprehensive technical information about the user's environment
 */
export const collectTechnicalInfo = async () => {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  let osName = 'Unknown';

  // Browser detection
  if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Edg') === -1) {
    browserName = 'Chrome';
    browserVersion = userAgent.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
    browserVersion = userAgent.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    browserVersion = userAgent.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Edg') > -1) {
    browserName = 'Edge';
    browserVersion = userAgent.match(/Edg\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browserName = 'Opera';
    browserVersion = userAgent.match(/(?:Opera|OPR)\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Trident') > -1) {
    browserName = 'Internet Explorer';
    browserVersion = userAgent.match(/rv:([0-9.]+)/)?.[1] || 'Unknown';
  }

  // OS detection
  if (userAgent.indexOf('Windows NT 10.0') > -1) osName = 'Windows 10/11';
  else if (userAgent.indexOf('Windows NT 6.3') > -1) osName = 'Windows 8.1';
  else if (userAgent.indexOf('Windows NT 6.2') > -1) osName = 'Windows 8';
  else if (userAgent.indexOf('Windows NT 6.1') > -1) osName = 'Windows 7';
  else if (userAgent.indexOf('Windows') > -1) osName = 'Windows';
  else if (userAgent.indexOf('Mac OS X') > -1) {
    const macVersion = userAgent.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    osName = macVersion ? `macOS ${macVersion}` : 'macOS';
  } else if (userAgent.indexOf('Linux') > -1) osName = 'Linux';
  else if (userAgent.indexOf('Android') > -1) {
    const androidVersion = userAgent.match(/Android ([0-9.]+)/)?.[1];
    osName = androidVersion ? `Android ${androidVersion}` : 'Android';
  } else if (userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) {
    const iosVersion = userAgent.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    osName = iosVersion ? `iOS ${iosVersion}` : 'iOS';
  }

  // Battery info
  let batteryInfo = 'unknown';
  try {
    if ('getBattery' in navigator) {
      const battery = await navigator.getBattery();
      batteryInfo = JSON.stringify({
        charging: battery.charging,
        level: `${Math.round(battery.level * 100)}%`,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime === Infinity ? 'unknown' : battery.dischargingTime
      });
    }
  } catch (e) {
    batteryInfo = 'not_supported';
  }

  // Device memory
  const deviceMemory = navigator.deviceMemory ? `${navigator.deviceMemory} GB` : 'unknown';

  // Media devices
  let mediaDevices = 'unknown';
  try {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInput = devices.filter(d => d.kind === 'audioinput').length;
      const audioOutput = devices.filter(d => d.kind === 'audiooutput').length;
      const videoInput = devices.filter(d => d.kind === 'videoinput').length;
      mediaDevices = JSON.stringify({
        total: devices.length,
        audioInput,
        audioOutput,
        videoInput,
        hasCamera: videoInput > 0,
        hasMicrophone: audioInput > 0,
        hasSpeakers: audioOutput > 0
      });
    }
  } catch (e) {
    mediaDevices = 'not_supported';
  }

  // Connection info
  let connectionInfo = 'unknown';
  if ('connection' in navigator || 'mozConnection' in navigator || 'webkitConnection' in navigator) {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    connectionInfo = JSON.stringify({
      effectiveType: conn.effectiveType || 'unknown',
      downlink: conn.downlink || 'unknown',
      rtt: conn.rtt || 'unknown',
      saveData: conn.saveData || false
    });
  }

  // Browser features
  const browserFeatures = JSON.stringify({
    webGL: !!window.WebGLRenderingContext,
    webGL2: !!window.WebGL2RenderingContext,
    webAssembly: typeof WebAssembly !== 'undefined',
    indexedDB: !!window.indexedDB,
    webWorkers: typeof Worker !== 'undefined',
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    webRTC: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    geolocation: 'geolocation' in navigator,
    notifications: 'Notification' in window,
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    webSockets: 'WebSocket' in window,
    fullscreen: document.fullscreenEnabled || false,
    pointerLock: 'pointerLockElement' in document,
    gamepad: 'getGamepads' in navigator,
    vibration: 'vibrate' in navigator,
    clipboard: 'clipboard' in navigator,
    share: 'share' in navigator
  });

  // Permissions status
  let permissionsStatus = 'unknown';
  try {
    if ('permissions' in navigator) {
      const permissions = await Promise.all([
        navigator.permissions.query({ name: 'camera' }).catch(() => ({ state: 'unknown' })),
        navigator.permissions.query({ name: 'microphone' }).catch(() => ({ state: 'unknown' })),
        navigator.permissions.query({ name: 'geolocation' }).catch(() => ({ state: 'unknown' })),
        navigator.permissions.query({ name: 'notifications' }).catch(() => ({ state: 'unknown' })),
        navigator.permissions.query({ name: 'persistent-storage' }).catch(() => ({ state: 'unknown' }))
      ]);
      permissionsStatus = JSON.stringify({
        camera: permissions[0].state,
        microphone: permissions[1].state,
        geolocation: permissions[2].state,
        notifications: permissions[3].state,
        'persistent-storage': permissions[4].state
      });
    }
  } catch (e) {
    permissionsStatus = 'not_supported';
  }

  // Storage quota
  let storageQuota = 'unknown';
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const quotaMB = estimate.quota ? Math.round(estimate.quota / 1024 / 1024) : 'unknown';
      const usageMB = estimate.usage ? Math.round(estimate.usage / 1024 / 1024) : 'unknown';
      const usagePercentage = estimate.quota && estimate.usage 
        ? Math.round((estimate.usage / estimate.quota) * 100) 
        : 'unknown';
      storageQuota = JSON.stringify({
        quota: `${quotaMB} MB`,
        usage: `${usageMB} MB`,
        usagePercentage: `${usagePercentage}%`
      });
    }
  } catch (e) {
    storageQuota = 'not_supported';
  }

  // Touch support
  const touchSupport = JSON.stringify({
    maxTouchPoints: navigator.maxTouchPoints || 0,
    touchSupported: 'ontouchstart' in window || navigator.maxTouchPoints > 0
  });

  // Orientation info
  let orientationInfo = 'unknown';
  if (window.screen.orientation) {
    orientationInfo = JSON.stringify({
      type: window.screen.orientation.type,
      angle: window.screen.orientation.angle
    });
  }

  // Platform info
  const platformInfo = JSON.stringify({
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    vendor: navigator.vendor,
    appName: navigator.appName,
    appVersion: navigator.appVersion
  });

  return {
    page_url: window.location.href,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    browser_name: browserName,
    browser_version: browserVersion,
    os_name: osName,
    battery_info: batteryInfo,
    device_memory: deviceMemory,
    media_devices: mediaDevices,
    online_status: navigator.onLine ? 'online' : 'offline',
    platform_info: platformInfo,
    storage_quota: storageQuota,
    touch_support: touchSupport,
    webrtc_support: (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ? 'supported' : 'not_supported',
    connection_info: connectionInfo,
    cookies_enabled: navigator.cookieEnabled ? 'yes' : 'no',
    browser_features: browserFeatures,
    orientation_info: orientationInfo,
    permissions_status: permissionsStatus,
    geolocation_support: 'geolocation' in navigator ? 'supported' : 'not_supported',
    hardware_concurrency: navigator.hardwareConcurrency || 'unknown',
    notification_support: 'Notification' in window ? Notification.permission : 'not_supported',
    local_storage_enabled: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return 'yes';
      } catch (e) {
        return 'no';
      }
    })(),
    service_worker_support: 'serviceWorker' in navigator ? 'supported' : 'not_supported'
  };
};

/**
 * Extract MTurk session ID from URL or localStorage
 */
export const getMTurkSessionId = () => {
  // Try to get from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const sessionFromUrl = urlParams.get('session_id') || urlParams.get('sessionId');
  
  if (sessionFromUrl) {
    return sessionFromUrl;
  }

  // Try to get from URL path (e.g., /process/session_id)
  const pathMatch = window.location.pathname.match(/\/(verification|process)\/([^/]+)/);
  if (pathMatch && pathMatch[2]) {
    return pathMatch[2];
  }

  // Try to get from localStorage
  const sessionFromStorage = localStorage.getItem('mturk_session_id') || 
                             localStorage.getItem('sessionId') ||
                             localStorage.getItem('session_token');
  
  if (sessionFromStorage) {
    return sessionFromStorage;
  }

  return null;
};

/**
 * Problem type options for the help form
 * @param {Object} translations - Translation object from useLanguage hook
 * @returns {Array} Array of problem type options with translated labels
 */
export const getProblemTypes = (translations) => {
  if (!translations || !translations.helpForm || !translations.helpForm.problemTypes) {
    // Fallback to English if translations are not available
    return [
      { value: 'verification_failed', label: 'Verification Failed' },
      { value: 'page_not_loading', label: 'Page Not Loading' },
      { value: 'button_not_working', label: 'Button Not Working' },
      { value: 'incorrect_language', label: 'Incorrect Language' },
      { value: 'mobile_issues', label: 'Mobile Issues' },
      { value: 'other', label: 'Other' }
    ];
  }

  const { problemTypes } = translations.helpForm;
  return [
    { value: 'verification_failed', label: problemTypes.verificationFailed },
    { value: 'page_not_loading', label: problemTypes.pageNotLoading },
    { value: 'button_not_working', label: problemTypes.buttonNotWorking },
    { value: 'incorrect_language', label: problemTypes.incorrectLanguage },
    { value: 'mobile_issues', label: problemTypes.mobileIssues },
    { value: 'other', label: problemTypes.other }
  ];
};

// Keep the old export for backward compatibility
export const PROBLEM_TYPES = [
  { value: 'verification_failed', label: 'Verification Failed' },
  { value: 'page_not_loading', label: 'Page Not Loading' },
  { value: 'button_not_working', label: 'Button Not Working' },
  { value: 'incorrect_language', label: 'Incorrect Language' },
  { value: 'mobile_issues', label: 'Mobile Issues' },
  { value: 'other', label: 'Other' }
];

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Check if user prefers high contrast
 */
export const prefersHighContrast = () => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

/**
 * Check if device is mobile
 */
export const isMobile = () => {
  return (
    window.matchMedia("(pointer: coarse)").matches &&
    window.matchMedia("(max-width: 1024px)").matches
  );
};
